/** Дерево «Подбора поставки»: товар (оффер: кабинет + offer_key) → его регионы (строки
 *  `wb_region_now` как есть). Числа товара — суммы по ВКЛЮЧЁННЫМ регионам («Куда планируем»):
 *  выключенный регион не даёт товару ни фактов, ни количества (как у «Распределения», aggValues);
 *  дни покрытия товара — из сумм (Σ остатка FBO+FBS / Σ спроса в день, правило 3 контракта).
 *  Фильтры отсекают ТОВАР целиком; «до N дней» и выгрузка ходят по показанным (или отмеченным
 *  показанным) товарам × включённым регионам.
 *
 *  Квант (draftQuant.ts): «Нужно» региона — введённое / «до N дней»; «С квантом» — оно вверх до
 *  кратного кванту товара, если включено «Округлять до кванта» (`round`), иначе = «Нужно». Суммы
 *  товара, сводка и выгрузка считают итог («С квантом») по каждому включённому региону. */
import type { SupplyDraftExportRow, WbOffer, WbOfferCell } from "@/api/wbRegions";
import { quantExportNote, quantOf, roundQty, type Quant, type QuantIndex } from "./draftQuant";
import type { Draft } from "./draftStorage";
import { isOn, type Targets } from "./draftTargets";
import { regionLabel } from "./matrix";
import { fillQty, leafKey, matchesQuery, MAX_QTY, productKey, regionId } from "./supplyDraft";

/** Регион товара. */
export interface DraftLeaf {
  /** null — у оффера нет `offer_key` (размер не определён): количества нет */
  key: string | null;
  cell: WbOfferCell;
  regionId: string;
  region: string;
  /** цель включена («Куда планируем») */
  on: boolean;
}

/** Товар: оффер кабинета. */
export interface DraftProduct {
  /** null — у оффера нет `offer_key`: не отмечается, количества нет */
  key: string | null;
  offer: WbOffer;
  /** квант по nm оффера (один на все размеры) */
  quant: Quant;
  leaves: DraftLeaf[];
}

/** `quants` null — квант из 1С не загружен (у всех товаров `unloaded`). */
export function buildProducts(offers: WbOffer[], targets: Targets, quants: QuantIndex | null): DraftProduct[] {
  return offers.map((o) => ({
    key: o.offer_key ? productKey(o.legal_name, o.offer_key) : null,
    offer: o,
    quant: quantOf(o, quants),
    leaves: o.regions.map((c) => {
      const id = regionId(c);
      return {
        key: o.offer_key ? leafKey(o.legal_name, o.offer_key, c.region_wb) : null,
        cell: c,
        regionId: id,
        region: regionLabel(c),
        on: isOn(targets, { id, macro: c.region_kind === "macro" }),
      };
    }),
  }));
}

/** «Нужно» региона в суммах и выгрузке: выключенный — 0, не введено — 0. */
export function leafQty(l: DraftLeaf, qty: Draft["qty"]): number {
  return l.on && l.key !== null ? (qty[l.key] ?? 0) : 0;
}

/** Итог региона («С квантом»): «Нужно» вверх до кванта товара при `round`, иначе «Нужно». */
export function leafFinal(p: DraftProduct, l: DraftLeaf, qty: Draft["qty"], round: boolean): number {
  return roundQty(leafQty(l, qty), p.quant, round);
}

/** Σ без NULL; из одних NULL (или пусто) — NULL, а не 0 (как `nsum` бэкенда). */
function nsum(values: (number | null)[]): number | null {
  const present = values.filter((v): v is number => v !== null);
  if (present.length === 0) return null;
  return Math.round(present.reduce((a, b) => a + b, 0) * 1e6) / 1e6;
}

export interface ProductAgg {
  orders_fbo_28d: number | null;
  fbo_stock: number | null;
  orders_28d: number | null;
  /** Σ stock_total / Σ orders_28d, 1 знак; 0 спроса или NULL — null */
  cover_days_total: number | null;
  /** Σ «Нужно» по включённым регионам */
  qty: number;
  /** Σ итога («С квантом») по включённым регионам — округляется каждый регион, не сумма */
  final: number;
}

export function aggregate(p: DraftProduct, qty: Draft["qty"], round: boolean): ProductAgg {
  const live = p.leaves.filter((l) => l.on);
  const stockTotal = nsum(live.map((l) => l.cell.stock_total));
  const orders = nsum(live.map((l) => l.cell.orders_28d));
  return {
    orders_fbo_28d: nsum(live.map((l) => l.cell.orders_fbo_28d)),
    fbo_stock: nsum(live.map((l) => l.cell.fbo_stock)),
    orders_28d: orders,
    cover_days_total: stockTotal === null || orders === null || orders === 0 ? null : Math.round((stockTotal / orders) * 10) / 10,
    qty: live.reduce((a, l) => a + leafQty(l, qty), 0),
    final: live.reduce((a, l) => a + leafFinal(p, l, qty, round), 0),
  };
}

export interface DraftFilters {
  q: string;
  /** есть включённый регион с `deficit_class` zero/low */
  deficitOnly: boolean;
  markedOnly: boolean;
}

export function isMarked(sel: Draft["sel"], p: DraftProduct): boolean {
  return p.key !== null && sel.has(p.key);
}

export function filterProducts(products: DraftProduct[], f: DraftFilters, sel: Draft["sel"]): DraftProduct[] {
  return products.filter((p) => {
    if (!matchesQuery(p.offer, f.q)) return false;
    if (f.deficitOnly && !p.leaves.some((l) => l.on && (l.cell.deficit_class === "zero" || l.cell.deficit_class === "low"))) {
      return false;
    }
    if (f.markedOnly && !isMarked(sel, p)) return false;
    return true;
  });
}

/** К чему применяется «до N дней»: есть отмеченные среди показанных — к ним, иначе ко всем показанным. */
export function fillTarget(shown: DraftProduct[], sel: Draft["sel"]): { products: DraftProduct[]; marked: boolean } {
  const marked = shown.filter((p) => isMarked(sel, p));
  return marked.length > 0 ? { products: marked, marked: true } : { products: shown, marked: false };
}

export interface FillResult {
  qty: Record<string, number>;
  /** регионов с количеством > 0 */
  filled: number;
  /** регионов, где вышло 0 (количество снято) */
  zero: number;
  /** NULL спрос или остаток — не заполнены, количество очищено */
  skipped: number;
  /** рассчитанное количество > MAX_QTY — не заполнены, количество очищено */
  overLimit: number;
  /** включённых регионов у товаров без offer_key — не заполняются */
  noKey: number;
}

/** Перезаписать количество включённых регионов `products` (см. supplyDraft.FILL_FORMULA); у регионов без данных
 *  и выше лимита количество очищается. */
export function fillToDays(products: DraftProduct[], qty: Draft["qty"], days: number): FillResult {
  const next: Record<string, number> = { ...qty };
  const r = { filled: 0, zero: 0, skipped: 0, overLimit: 0, noKey: 0 };
  for (const p of products) {
    for (const l of p.leaves) {
      if (!l.on) continue;
      if (l.key === null) {
        r.noKey += 1;
        continue;
      }
      const q = fillQty(l.cell, days);
      // нет данных / выше лимита: старое количество не оставляем (иначе оно уйдёт в xlsx как «до N дней»)
      if (q === null) {
        delete next[l.key];
        r.skipped += 1;
      } else if (q > MAX_QTY) {
        delete next[l.key];
        r.overLimit += 1;
      } else if (q > 0) {
        next[l.key] = q;
        r.filled += 1;
      } else {
        delete next[l.key];
        r.zero += 1;
      }
    }
  }
  return { qty: next, ...r };
}

export type ExportMode = "shown" | "selected";

/** Что уходит в выгрузку: `shown` — показанные товары, `selected` — отмеченные И показанные
 *  (отмеченный, но спрятанный фильтром товар не уходит — как у фиксации «Распределения»). */
export function exportProducts(shown: DraftProduct[], sel: Draft["sel"], mode: ExportMode): DraftProduct[] {
  return mode === "shown" ? shown : shown.filter((p) => isMarked(sel, p));
}

/** Строки xlsx: товар × включённый регион с «Нужно» > 0; `qty` — итог (с квантом при `round`),
 *  `qty_need` — «Нужно», `in_box` — квант (null — нет), `in_box_note` — почему нет. */
export function exportRows(products: DraftProduct[], qty: Draft["qty"], round: boolean): SupplyDraftExportRow[] {
  const out: SupplyDraftExportRow[] = [];
  for (const p of products) {
    const o = p.offer;
    for (const l of p.leaves) {
      const q = leafQty(l, qty);
      if (q <= 0) continue;
      out.push({
        legal_name: o.legal_name,
        region: l.region,
        vendor_code: o.vendor_code,
        mp_sku: o.mp_sku,
        mp_size: o.mp_size,
        article_1c: o.article_1c,
        name: o.name ?? o.name_1c,
        qty: roundQty(q, p.quant, round),
        qty_need: q,
        in_box: p.quant.kind === "ok" ? p.quant.inBox : null,
        in_box_note: quantExportNote(p.quant),
        orders_fbo_per_day: l.cell.orders_fbo_28d,
        fbo_stock: l.cell.fbo_stock,
        cover_days: l.cell.cover_days_total,
      });
    }
  }
  return out;
}

export interface DraftSummary {
  /** отмеченных товаров в данных вкладки */
  marked: number;
  /** из них показано (прошли фильтры) */
  markedShown: number;
  /** отмечено в черновике, но товара нет в данных (другой кабинет / пропал из витрины) */
  outside: number;
  /** Σ шт. (итог) показанных товаров по включённым регионам */
  shownQty: number;
  /** Σ шт. (итог) отмеченных показанных */
  markedQty: number;
  /** по регионам — у показанных товаров (итог) */
  byRegion: { region: string; rows: number; qty: number }[];
  /** показанных товаров без кванта (не задан в спеке / nm нет в 1С) */
  quantUnset: number;
  /** показанных товаров с конфликтом квантов */
  quantConflict: number;
}

export function summarize(products: DraftProduct[], shown: DraftProduct[], draft: Draft, round: boolean): DraftSummary {
  const present = new Set(products.map((p) => p.key).filter((k): k is string => k !== null));
  const regions = new Map<string, { rows: number; qty: number }>();
  let shownQty = 0;
  let markedQty = 0;
  let markedShown = 0;
  let quantUnset = 0;
  let quantConflict = 0;
  for (const p of shown) {
    const marked = isMarked(draft.sel, p);
    if (marked) markedShown += 1;
    if (p.quant.kind === "unset" || p.quant.kind === "no-nm") quantUnset += 1;
    if (p.quant.kind === "conflict") quantConflict += 1;
    for (const l of p.leaves) {
      const q = leafFinal(p, l, draft.qty, round);
      if (q <= 0) continue;
      shownQty += q;
      if (marked) markedQty += q;
      const g = regions.get(l.region) ?? { rows: 0, qty: 0 };
      g.rows += 1;
      g.qty += q;
      regions.set(l.region, g);
    }
  }
  const marked = products.filter((p) => isMarked(draft.sel, p)).length;
  return {
    marked,
    markedShown,
    outside: [...draft.sel].filter((k) => !present.has(k)).length,
    shownQty,
    markedQty,
    byRegion: [...regions].map(([region, g]) => ({ region, ...g })).sort((a, b) => b.qty - a.qty),
    quantUnset,
    quantConflict,
  };
}

/** Ввод количества региона: пусто — запись удаляется. */
export function setLeafQty(qty: Draft["qty"], key: string, v: number | null): Record<string, number> {
  const next: Record<string, number> = { ...qty };
  if (v === null) delete next[key];
  else next[key] = v;
  return next;
}

export function toggleSel(sel: Draft["sel"], key: string): Set<string> {
  const next = new Set(sel);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}
